import type { Option } from "../../../components/Select/Select";
import type { BotGrowthRule, BotsScoreMode } from "../config/types";
import type { Leaderboard } from "../../leaderboards/types";

/** What the Leaderboards grid needs to know about a project's board. */
export type BoardShape = Pick<Leaderboard, "ext_key" | "operator" | "sort">;

/**
 * How a row's `score from` cell is drawn: a choice — of the random draws the
 * board admits and, when a stat grows, of the stats it can follow — or the
 * mode the board fixes.
 */
export type ScoreModeCell =
  | {
      kind: "select";
      draws: BotsScoreMode[];
      /** What a board following a stat is sent: its new value, or the gain. */
      follows: "value" | "gain";
    }
  | { kind: "fixed"; label: "Gain" | "Result"; title: string };

/** Prefix of a stat option's value, so a stat named `gain` is not the draw. */
export const STAT_OPTION = "stat:";

/**
 * The server decides from the board's operator what a submit means; this is
 * the same decision, made so the grid offers a choice only where one exists.
 * An increment board adds what it receives, so it can follow a stat by its
 * gains; a board where lower wins can follow nothing that climbs. A board the
 * list does not know keeps the full Select, because nothing says the saved
 * value is wrong.
 */
export function scoreModeCell(
  board: BoardShape | undefined,
  growing: boolean,
): ScoreModeCell {
  if (board?.operator === "increment") {
    return growing
      ? { kind: "select", draws: ["gain"], follows: "gain" }
      : {
          kind: "fixed",
          label: "Gain",
          title: "The board adds every submit itself",
        };
  }
  if (board?.operator === "best" && board.sort === "asc") {
    return {
      kind: "fixed",
      label: "Result",
      title:
        "Lower is better on this board: neither a gain nor a growing stat can rank here",
    };
  }
  if (board?.operator === "worst") {
    return {
      kind: "fixed",
      label: "Result",
      title:
        "This board keeps the worse score: neither a gain nor a growing stat can rank here",
    };
  }
  return { kind: "select", draws: ["result", "gain"], follows: "value" };
}

const DRAW_LABELS: Record<BotsScoreMode, string> = {
  result: "Result",
  gain: "Gain",
};

/**
 * The `score from` list: the draws, a separator, then every stat of
 * `bots.growth[]` with how it grows. No stat grows — no separator, no group.
 */
export function scoreSourceOptions(
  draws: BotsScoreMode[],
  growth: BotGrowthRule[],
): Option[] {
  const stats = growth.filter((g) => g.key.trim() !== "");
  return [
    ...draws.map((d, i) => ({
      value: d,
      label: DRAW_LABELS[d],
      separatorAfter: i === draws.length - 1 && stats.length > 0,
    })),
    ...stats.map((g) => ({
      value: `${STAT_OPTION}${g.key}`,
      label: g.key,
      mono: true,
      meta: g.mode === "per_day" ? "per day" : "during play",
    })),
  ];
}
